import { z } from "zod";

/**
 * Wire-shape mirror of the shore's recovery schedules (PAYD-FR-044, FR-045,
 * FR-125): `RecoveryScheduleDto`, `RecoveryScheduleDetailDto` and the request
 * bodies `recovery-schedule.controller.ts` takes.
 *
 * Money is a string in both directions, the same reason `payroll-inputs`
 * gives: `Decimal(15, 2)` on the shore, and parsing it here would undo the
 * precision the field exists to protect.
 */

/// `recovery-instalment.ts` RECOVERY_TYPES, copied.
export const RECOVERY_TYPES = ["recovery", "advance", "court_order"] as const;
export type RecoveryType = (typeof RECOVERY_TYPES)[number];

/// `recovery-instalment.ts` RECOVERY_STATUSES, copied.
export const RECOVERY_STATUSES = ["active", "paused", "closed", "settled", "cancelled"] as const;
export type RecoveryStatus = (typeof RECOVERY_STATUSES)[number];

export const DECIMAL_15_2 = /^\d{1,13}(\.\d{1,2})?$/;
export const CURRENCY_CODE = /^[A-Z]{3}$/;
export const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
export const AMOUNT_MESSAGE = "Use an amount with at most 2 decimal places.";
export const DATE_MESSAGE = "Use a date in YYYY-MM-DD format.";
const CURRENCY = CURRENCY_CODE;

export const recoveryInstalmentSchema = z
  .object({
    rowId: z.string(),
    cycleStart: z.string(),
    asked: z.string(),
    /// Null while no live slip has priced it yet.
    taken: z.string().nullable(),
  })
  .strict();

export type RecoveryInstalment = z.infer<typeof recoveryInstalmentSchema>;

export const recoveryScheduleSchema = z
  .object({
    id: z.string(),
    membershipId: z.string(),
    /**
     * Who owes this, named by the shore.
     *
     * Read straight off the row rather than joined against `useSalaryOverview`,
     * whose own doc calls it "every LIVE member": it drops anyone whose
     * relieving date has passed and anyone whose membership an exit
     * soft-deleted. An outstanding advance or notice-pay shortfall is exactly
     * what OUTLIVES an exit, so that join missed the rows it mattered most on
     * and the list printed "Member" in a person's place. It also emptied this
     * page's name search, which matched on the resolved name.
     */
    memberName: z.string(),
    componentId: z.string(),
    type: z.string(),
    total: z.string(),
    instalmentAmount: z.string(),
    currency: z.string(),
    startCycle: z.string(),
    reason: z.string(),
    attachmentId: z.string().nullable(),
    advanceRowId: z.string().nullable(),
    orderReference: z.string().nullable(),
    payeeName: z.string().nullable(),
    payeeBank: z.string().nullable(),
    priority: z.number(),
    status: z.string(),
    statusReason: z.string().nullable(),
    /// Derived from live slips, never stored (PAYD-FR-045).
    recovered: z.string(),
    outstanding: z.string(),
    carriedForward: z.string(),
    version: z.number(),
  })
  .strict();

export type RecoverySchedule = z.infer<typeof recoveryScheduleSchema>;

export const recoveryScheduleDetailSchema = recoveryScheduleSchema
  .extend({ instalments: z.array(recoveryInstalmentSchema) })
  .strict();

export type RecoveryScheduleDetail = z.infer<typeof recoveryScheduleDetailSchema>;

export const recoverySchedulesPageSchema = z
  .object({
    rows: z.array(recoveryScheduleSchema),
    nextCursor: z.string().nullable(),
  })
  .strict();

export type RecoverySchedulesPage = z.infer<typeof recoverySchedulesPageSchema>;

/// `CreateRecoveryScheduleDto`. `advanceComponentId` + `advancePayrollDate`
/// name an earning component to pay out now, creating the money-out row in
/// the same call as the plan to take it back (PAYD-FR-044).
export const createRecoveryScheduleBodySchema = z
  .object({
    membershipId: z.string().min(1).max(40),
    componentId: z.string().min(1).max(40),
    type: z.enum(RECOVERY_TYPES).optional(),
    total: z.string().regex(DECIMAL_15_2, AMOUNT_MESSAGE),
    instalmentAmount: z.string().regex(DECIMAL_15_2, AMOUNT_MESSAGE),
    currency: z.string().regex(CURRENCY, "Use a 3-letter currency code."),
    startCycle: z.string().regex(ISO_DATE, DATE_MESSAGE),
    reason: z.string().min(3).max(500),
    attachmentId: z.string().min(1).max(40).optional(),
    /// PAYD-FR-125. Required for a court order, meaningless without one.
    orderReference: z.string().min(1).max(120).optional(),
    payeeName: z.string().min(1).max(120).optional(),
    payeeBank: z.string().min(1).max(120).optional(),
    advanceComponentId: z.string().min(1).max(40).optional(),
    advancePayrollDate: z.string().regex(ISO_DATE, DATE_MESSAGE).optional(),
  })
  .strict();

export type CreateRecoveryScheduleBody = z.infer<typeof createRecoveryScheduleBodySchema>;

/// `RecoveryScheduleStatusDto`. Pause, settle and cancel all take one shape:
/// the version being acted on, and why. "It just stopped" is not an answer
/// the member reading their payslip can be given.
export const recoveryScheduleStatusBodySchema = z
  .object({
    version: z.number().int().min(0),
    reason: z.string().min(3).max(500),
  })
  .strict();

export type RecoveryScheduleStatusBody = z.infer<typeof recoveryScheduleStatusBodySchema>;

export const resumeRecoveryScheduleBodySchema = z
  .object({ version: z.number().int().min(0) })
  .strict();

export type ResumeRecoveryScheduleBody = z.infer<typeof resumeRecoveryScheduleBodySchema>;

export const generateInstalmentsBodySchema = z
  .object({ cycleStart: z.string().regex(ISO_DATE, DATE_MESSAGE) })
  .strict();

export type GenerateInstalmentsBody = z.infer<typeof generateInstalmentsBodySchema>;

export const generateInstalmentsResultSchema = z
  .object({ created: z.number(), skipped: z.number(), closed: z.number() })
  .strict();

export type GenerateInstalmentsResult = z.infer<typeof generateInstalmentsResultSchema>;
